import { useMemo } from "react";
import { useAppSelector } from "../../Redux/hooks";
import { permissions } from "../../utils/roles";

/**
 * What the current user may do with randomization in this study.
 *
 * ONE HOOK, READ IN EVERY RANDOMIZATION SCREEN, and the reason is spec 21.8's
 * rule: a control the user may not use is NOT RENDERED — never rendered and
 * disabled. A disabled button tells the user the capability exists and that
 * they are being denied it, which on a blinded study is itself information
 * (that there is an arm to see) and on every study is an invitation to ask an
 * administrator for a permission they should not have.
 *
 * The flags are derived from `state.study.studyPermissions`, the list the
 * server sent for THIS study, so a permission granted on another study never
 * leaks in. Nothing here is a security control — every one of these checks is
 * enforced again server-side, and the server is the only place it counts. This
 * exists so the screen does not offer actions that would be refused.
 *
 * `featureEnabled` is separate from every permission: a study with the feature
 * switched off shows the explanatory state of spec 21.1 even to a user holding
 * every randomization permission there is.
 *
 * @category Randomization
 */
export interface RandomizationAccess {
  featureEnabled: boolean;
  mrtFeatureEnabled: boolean;
  testBenchFeatureEnabled: boolean;

  canViewSettings: boolean;
  canManageSettings: boolean;
  canGenerateSequence: boolean;
  canGenerateStrata: boolean;

  /**
   * The configuration lifecycle (SFT-12, SFT-13, SFT-20).
   *
   * `canManageConfig` covers building the design, committing it and putting it
   * in force. Withdrawal and adding a site stratum have their own permissions
   * because each is an action a study may want in different hands: withdrawal
   * destroys a generated sequence, and a site addition is the only sanctioned
   * change to a LIVE configuration — a study can grant it to whoever opens
   * sites without granting them the design.
   */
  canManageConfig: boolean;
  canWithdrawConfig: boolean;
  canAddSiteStratum: boolean;

  canViewList: boolean;
  canUploadList: boolean;
  canDownloadList: boolean;

  canAllocate: boolean;
  canViewAllocation: boolean;
  canViewArm: boolean;
  canReverse: boolean;
  canUnblind: boolean;

  canManageMrt: boolean;
  canViewMrt: boolean;

  canExport: boolean;
  canUseTestBench: boolean;

  canViewSeed: boolean;
  canViewStratumLabels: boolean;
  canViewReasons: boolean;
  canViewUnblindings: boolean;
  canViewUnblindingDisclosure: boolean;
}

export function useRandomizationAccess(): RandomizationAccess {
  const studyPermissions = useAppSelector(
    (state) => state.study.studyPermissions,
  );
  const features = useAppSelector((state) => state.study.features);

  return useMemo(() => {
    const held = (code: string) =>
      Array.isArray(studyPermissions) && studyPermissions.includes(code);

    return {
      // `randomization` is the parent feature; the two children gate the MRT
      // and test-bench screens independently, so a study can randomize
      // without exposing either.
      featureEnabled: features?.randomization === true,
      mrtFeatureEnabled:
        features?.randomization === true && features?.["randomization.mrt"] === true,
      testBenchFeatureEnabled:
        features?.randomization === true &&
        features?.["randomization.testbench"] === true,

      canViewSettings: held(permissions.randomizationSettingsView),
      canManageSettings: held(permissions.randomizationSettingsManage),
      canGenerateSequence: held(permissions.randomizationSequenceGenerate),
      canGenerateStrata: held(permissions.randomizationStrataGenerate),

      // EITHER `config_manage` OR `settings_manage`. The two sit alongside
      // each other until the retirement release, so a study whose roles were
      // set up before 12 September 2026 holds only the older code — and
      // requiring the new one alone would silently remove the configuration
      // screen from everyone who had it yesterday.
      canManageConfig:
        held(permissions.randomizationConfigManage) ||
        held(permissions.randomizationSettingsManage),
      canWithdrawConfig: held(permissions.randomizationConfigWithdraw),
      canAddSiteStratum: held(permissions.randomizationSiteStratumAdd),

      canViewList: held(permissions.randomizationListView),
      canUploadList: held(permissions.randomizationListUpload),
      canDownloadList: held(permissions.randomizationListDownload),

      canAllocate: held(permissions.randomizationAllocate),
      canViewAllocation: held(permissions.randomizationAllocationView),
      canViewArm: held(permissions.randomizationArmView),
      canReverse: held(permissions.randomizationReverse),
      canUnblind: held(permissions.randomizationUnblind),

      canManageMrt: held(permissions.randomizationMrtManage),
      canViewMrt: held(permissions.randomizationMrtView),

      canExport: held(permissions.randomizationExport),
      canUseTestBench: held(permissions.randomizationTestbench),

      canViewSeed: held(permissions.randomizationSeedView),
      canViewStratumLabels: held(permissions.randomizationStratumView),
      canViewReasons: held(permissions.randomizationReasonView),
      canViewUnblindings: held(permissions.randomizationUnblindingView),
      canViewUnblindingDisclosure: held(
        permissions.randomizationUnblindingDisclosureView,
      ),
    };
  }, [studyPermissions, features]);
}
